// Centralized menu configuration for dynamic permissions
import {
  HomeIcon,
  UserGroupIcon,
  ClockIcon,
  CalendarIcon,
  CalendarDaysIcon,
  CurrencyPoundIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CogIcon,
  UsersIcon,
  DeviceTabletIcon,
  BuildingOfficeIcon,
  KeyIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  MegaphoneIcon,
  ArrowPathRoundedSquareIcon,
} from '@heroicons/react/24/outline';

// Icon mapping for dynamic navigation
export const iconMap = {
  HomeIcon: HomeIcon,
  UserGroupIcon: UserGroupIcon,
  ClockIcon: ClockIcon,
  CalendarIcon: CalendarIcon,
  CalendarDaysIcon: CalendarDaysIcon,
  CurrencyPoundIcon: CurrencyPoundIcon,
  DocumentTextIcon: DocumentTextIcon,
  ChartBarIcon: ChartBarIcon,
  CogIcon: CogIcon,
  UsersIcon: UsersIcon,
  DeviceTabletIcon: DeviceTabletIcon,
  BuildingOfficeIcon: BuildingOfficeIcon,
  KeyIcon: KeyIcon,
  AcademicCapIcon: AcademicCapIcon,
  CheckCircleIcon: CheckCircleIcon,
  ClipboardDocumentListIcon: ClipboardDocumentListIcon,
  ShieldCheckIcon: ShieldCheckIcon,
  ExclamationTriangleIcon: ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon: ClipboardDocumentCheckIcon,
  MegaphoneIcon: MegaphoneIcon,
  ArrowPathRoundedSquareIcon: ArrowPathRoundedSquareIcon,
};

// Main menu configuration - this is the source of truth for all menu items
export const menuConfig = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    href: '/',
    icon: 'HomeIcon',
    module: 'dashboard',
    permissions: ['view'],
    description: 'View system overview and key metrics'
  },
  {
    id: 'announcements',
    name: 'Announcements',
    href: '/announcements',
    icon: 'MegaphoneIcon',
    module: 'announcements',
    permissions: ['view', 'create', 'edit', 'delete'],
    description: 'Manage company announcements and notifications'
  },
  {
    id: 'organization',
    name: 'Organization',
    href: '/organization',
    icon: 'BuildingOfficeIcon',
    module: 'organization',
    permissions: ['view', 'create', 'edit', 'delete'],
    description: 'Manage company structure, departments, and divisions',
    subItems: [
      {
        id: 'organization-companies',
        name: 'Company Structure',
        href: '/organization/companies',
        module: 'organization',
        subPermission: 'companies'
      },
      {
        id: 'organization-departments',
        name: 'Departments',
        href: '/organization/departments',
        module: 'organization',
        subPermission: 'departments'
      },
      {
        id: 'organization-divisions',
        name: 'Divisions',
        href: '/organization/divisions',
        module: 'organization',
        subPermission: 'divisions'
      },
      {
        id: 'organization-hierarchy',
        name: 'Hierarchy View',
        href: '/organization/hierarchy',
        module: 'organization',
        subPermission: 'hierarchy'
      },
      {
        id: 'organization-roles',
        name: 'Role Management',
        href: '/organization/roles',
        module: 'organization',
        subPermission: 'roles'
      }
    ]
  },
  {
    id: 'employees',
    name: 'Employees',
    href: '/employees',
    icon: 'UserGroupIcon',
    module: 'employees',
    permissions: ['view', 'create', 'edit', 'delete'],
    description: 'Manage employee records and information'
  },
  {
    id: 'user-management',
    name: 'User Management',
    href: '/user-management',
    icon: 'UsersIcon',
    module: 'user_management',
    permissions: ['view', 'create', 'edit', 'delete'],
    description: 'Manage users, roles and system access'
  },
  {
    id: 'attendance',
    name: 'Attendance',
    href: '/attendance',
    icon: 'ClockIcon',
    module: 'attendance',
    permissions: ['view', 'create', 'edit'],
    description: 'View and manage personal attendance records'
  },
  {
    id: 'attendance-management',
    name: 'Attendance Management',
    href: '/attendance-management',
    icon: 'ClockIcon',
    module: 'attendance_management',
    permissions: ['view', 'create', 'edit', 'delete', 'approve'],
    description: 'Manage all employee attendance records'
  },
  {
    id: 'schedules',
    name: 'Schedules',
    href: '/schedules',
    icon: 'CalendarDaysIcon',
    module: 'schedules',
    permissions: ['view', 'create', 'edit', 'delete'],
    description: 'Manage work schedules and shifts'
  },
  {
    id: 'uk-compliance',
    name: 'UK Compliance',
    href: '/uk-compliance-dashboard',
    icon: 'ShieldCheckIcon',
    module: 'uk_compliance',
    permissions: ['view', 'monitor'],
    description: 'UK employment law compliance dashboard and monitoring'
  },
  {
    id: 'uk-scheduling',
    name: 'UK Scheduling',
    href: '/uk-scheduling',
    icon: 'CalendarDaysIcon',
    module: 'uk_scheduling',
    permissions: ['view', 'create', 'edit', 'delete'],
    description: 'Advanced scheduling with UK compliance validation',
    subItems: [
      {
        id: 'uk-scheduling-rotas',
        name: 'Weekly Rotas',
        href: '/uk-scheduling#rotas',
        module: 'uk_scheduling',
        subPermission: 'rotas'
      },
      {
        id: 'uk-scheduling-shifts',
        name: 'Shift Management',
        href: '/uk-scheduling#shifts',
        module: 'uk_scheduling',
        subPermission: 'shifts'
      },
      {
        id: 'uk-scheduling-compliance',
        name: 'Compliance Check',
        href: '/uk-scheduling#compliance',
        module: 'uk_scheduling',
        subPermission: 'compliance'
      }
    ]
  },
  {
    id: 'time-attendance',
    name: 'Time & Attendance',
    href: '/time-attendance',
    icon: 'ClipboardDocumentCheckIcon',
    module: 'time_attendance',
    permissions: ['view', 'create', 'edit', 'approve'],
    description: 'Enhanced time tracking with UK compliance monitoring',
    subItems: [
      {
        id: 'time-attendance-clock',
        name: 'Clock In/Out',
        href: '/time-attendance#clock',
        module: 'time_attendance',
        subPermission: 'clock'
      },
      {
        id: 'time-attendance-timesheets',
        name: 'Timesheets',
        href: '/time-attendance#timesheets',
        module: 'time_attendance',
        subPermission: 'timesheets'
      },
      {
        id: 'time-attendance-approval',
        name: 'Timesheet Approval',
        href: '/time-attendance#approval',
        module: 'time_attendance',
        subPermission: 'approval'
      },
      {
        id: 'time-attendance-overtime',
        name: 'Overtime Management',
        href: '/time-attendance#overtime',
        module: 'time_attendance',
        subPermission: 'overtime'
      }
    ]
  },
  {
    id: 'leave-requests',
    name: 'Leave Requests',
    href: '/leaves',
    icon: 'CalendarIcon',
    module: 'leave_requests',
    permissions: ['view', 'create', 'edit', 'delete', 'approve'],
    description: 'Manage leave requests and approvals'
  },
  {
    id: 'calendar-sync',
    name: 'Calendar Sync',
    href: '/calendar-sync',
    icon: 'ArrowPathRoundedSquareIcon',
    module: 'calendar_sync',
    permissions: ['view', 'manage'],
    description: 'Sync calendar with Google Calendar and Outlook'
  },
  {
    id: 'payroll',
    name: 'Payroll',
    href: '/payroll',
    icon: 'CurrencyPoundIcon',
    module: 'payroll',
    permissions: ['view', 'create', 'edit', 'process'],
    description: 'Manage payroll processing and records'
  },
  {
    id: 'hmrc',
    name: 'HMRC Compliance',
    href: '/hmrc',
    icon: 'DocumentTextIcon',
    module: 'hmrc',
    permissions: ['view', 'create', 'edit', 'export'],
    description: 'HMRC compliance and tax reporting'
  },
  {
    id: 'documents',
    name: 'Documents',
    href: '/documents',
    icon: 'DocumentTextIcon',
    module: 'documents',
    permissions: ['view', 'create', 'edit', 'delete', 'download'],
    description: 'Document management and storage'
  },
  {
    id: 'reports',
    name: 'Reports',
    href: '/reports',
    icon: 'ChartBarIcon',
    module: 'reports',
    permissions: ['view', 'create', 'export'],
    description: 'Generate and view system reports'
  },
  {
    id: 'kiosk',
    name: 'Kiosk Mode',
    href: '/kiosk',
    icon: 'DeviceTabletIcon',
    module: 'kiosk',
    permissions: ['view', 'manage'],
    description: 'Kiosk mode for employee check-in/out'
  },
  {
    id: 'permission-settings',
    name: 'Permission Settings',
    href: '/permission-settings',
    icon: 'KeyIcon',
    module: 'permission_settings',
    permissions: ['view', 'create', 'edit', 'delete'],
    description: 'Manage user roles and permissions'
  },
  {
    id: 'probationary',
    name: 'Probationary Management',
    href: '/probationary',
    icon: 'AcademicCapIcon',
    module: 'probation',
    permissions: ['view', 'create', 'edit', 'delete'],
    description: 'Manage employee probationary periods and reviews'
  },
  {
    id: 'workflow-management',
    name: 'Approval Workflows',
    href: '/workflow-management',
    icon: 'ClipboardDocumentListIcon',
    module: 'workflow',
    permissions: ['view', 'create', 'edit', 'delete', 'manage'],
    description: 'Manage approval workflow templates and configurations',
    subItems: [
      {
        id: 'workflow-templates',
        name: 'Workflow Templates',
        href: '/workflow-management/templates',
        module: 'workflow',
        subPermission: 'templates'
      },
      {
        id: 'workflow-history',
        name: 'Approval History',
        href: '/workflow-management/history',
        module: 'workflow',
        subPermission: 'history'
      }
    ]
  },
  {
    id: 'pending-approvals',
    name: 'Pending Approvals',
    href: '/pending-approvals',
    icon: 'CheckCircleIcon',
    module: 'workflow',
    permissions: ['view', 'update'],
    description: 'Review and process pending approval requests'
  },
  {
    id: 'settings',
    name: 'Settings',
    href: '/settings',
    icon: 'CogIcon',
    module: 'settings',
    permissions: ['view', 'edit'],
    description: 'System configuration and settings',
    subItems: [
      {
        id: 'sso-settings',
        name: 'SSO Configuration',
        href: '/sso-settings',
        module: 'settings',
        subPermission: 'sso'
      }
    ]
  }
];

// Get all menu items (including sub-items) for permissions management
export const getAllMenuItems = () => {
  const allItems = [];
  
  menuConfig.forEach(item => {
    // Add main item
    allItems.push({
      id: item.id,
      name: item.name,
      module: item.module,
      permissions: item.permissions,
      description: item.description,
      isSubItem: false
    });
    
    // Add sub-items if they exist
    if (item.subItems) {
      item.subItems.forEach(subItem => {
        allItems.push({
          id: subItem.id,
          name: `${item.name} - ${subItem.name}`,
          module: subItem.module,
          permissions: item.permissions, // Inherit parent permissions
          description: `${item.description} - ${subItem.name}`,
          isSubItem: true,
          parentId: item.id,
          subPermission: subItem.subPermission
        });
      });
    }
  });
  
  return allItems;
};

// Get menu items filtered by user permissions
export const getMenuItemsForUser = (userPermissions, userRole) => {
  return menuConfig.filter(item => {
    // Check if user has permission to view this menu item
    const modulePermissions = userPermissions.filter(p => p.module === item.module);
    const hasViewPermission = modulePermissions.some(p => p.action === 'view' && p.granted);
    
    return hasViewPermission;
  }).map(item => ({
    ...item,
    icon: iconMap[item.icon] || HomeIcon,
    // Filter sub-items based on permissions too
    subItems: item.subItems ? item.subItems.filter(subItem => {
      const subModulePermissions = userPermissions.filter(p => 
        p.module === subItem.module && p.action === 'view' && p.granted
      );
      return subModulePermissions.length > 0;
    }) : undefined
  }));
};

// Get all unique modules for permissions setup
export const getAllModules = () => {
  const modules = new Set();
  
  menuConfig.forEach(item => {
    modules.add(item.module);
    if (item.subItems) {
      item.subItems.forEach(subItem => {
        modules.add(subItem.module);
      });
    }
  });
  
  return Array.from(modules);
};